// Badges.js - Badge display component
import { useState, useEffect } from 'react';
import api from '../../Services/Api';
import styles from './Badges.module.css';

function Badges({ userId, showOnlyEarned = false }) {
    const [badgeData, setBadgeData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');

    useEffect(() => {
        fetchBadges();
        // eslint-disable-next-line
    }, [userId]);

    const fetchBadges = async () => {
        try {
            setLoading(true);
            // Pass user_id as query param if provided
            const url = userId ? `badges/?user_id=${userId}` : 'badges/';
            const response = await api.get(url);
            setBadgeData(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to load badges');
            console.error('Error fetching badges:', err);
        } finally {
            setLoading(false);
        }
    };

    const getFilteredBadges = () => {
        if (!badgeData?.all_badges) return [];
        
        if (activeFilter === 'all') return badgeData.all_badges;
        if (activeFilter === 'earned') return badgeData.all_badges.filter(b => b.earned);
        if (activeFilter === 'locked') return badgeData.all_badges.filter(b => !b.earned);
        
        // Filter by badge type
        return badgeData.all_badges.filter(b => b.badge_type === activeFilter);
    };

    const getBadgeTypeLabel = (type) => {
        const labels = {
            'streak': '🔥 Streak Badges',
            'clean_days': '✨ Clean Days',
            'consistency': '📊 Consistency',
            'social': '👥 Social',
            'special': '⭐ Special'
        };
        return labels[type] || type;
    };

    if (loading) {
        return (
            <div className={showOnlyEarned ? styles.simpleContainer : styles.container}>
                <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <p>Loading badges...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={showOnlyEarned ? styles.simpleContainer : styles.container}>
                <div className={styles.error}>
                    <p>{error}</p>
                    <button onClick={fetchBadges} className={styles.retryBtn}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    const filteredBadges = getFilteredBadges();

    // If showOnlyEarned is true, only show earned badges in a simple view
    if (showOnlyEarned) {
        const earnedBadges = badgeData?.earned_badges || [];
        
        return (
            <div className={styles.simpleContainer}>
                {earnedBadges.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>No badges earned yet</p>
                    </div>
                ) : (
                    <div className={styles.simpleBadgesGrid}>
                        {earnedBadges.map((badge) => (
                            <div key={badge.code} className={styles.simpleBadgeCard}>
                                <div className={styles.simpleBadgeIcon}>
                                    {badge.icon}
                                </div>
                                <div className={styles.simpleBadgeInfo}>
                                    <h4 className={styles.simpleBadgeName}>{badge.name}</h4>
                                    <p className={styles.simpleBadgeDescription}>{badge.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <h1 className={styles.title}>🏆 Achievements</h1>
                <p className={styles.subtitle}>Collect badges and track your progress</p>
            </div>

            {/* Summary Card */}
            <div className={styles.summaryCard}>
                <div className={styles.summaryGrid}>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryValue}>{badgeData?.summary?.earned_count || 0}</span>
                        <span className={styles.summaryLabel}>Earned</span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryValue}>{badgeData?.summary?.total_badges || 0}</span>
                        <span className={styles.summaryLabel}>Total</span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryValue}>{badgeData?.summary?.completion_percentage || 0}%</span>
                        <span className={styles.summaryLabel}>Complete</span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryValue}>{badgeData?.summary?.total_points || 0}</span>
                        <span className={styles.summaryLabel}>Points</span>
                    </div>
                </div>
                
                {/* Progress bar */}
                <div className={styles.progressBarContainer}>
                    <div 
                        className={styles.progressBar}
                        style={{ width: `${badgeData?.summary?.completion_percentage || 0}%` }}
                    ></div>
                </div>
            </div>

            {/* Filter Tabs */}
            <div className={styles.filterTabs}>
                <button 
                    className={`${styles.filterTab} ${activeFilter === 'all' ? styles.active : ''}`}
                    onClick={() => setActiveFilter('all')}
                >
                    All
                </button>
                <button 
                    className={`${styles.filterTab} ${activeFilter === 'earned' ? styles.active : ''}`}
                    onClick={() => setActiveFilter('earned')}
                >
                    ✅ Earned
                </button>
                <button 
                    className={`${styles.filterTab} ${activeFilter === 'locked' ? styles.active : ''}`}
                    onClick={() => setActiveFilter('locked')}
                >
                    🔒 Locked
                </button>
                <button 
                    className={`${styles.filterTab} ${activeFilter === 'streak' ? styles.active : ''}`}
                    onClick={() => setActiveFilter('streak')}
                >
                    🔥 Streak
                </button>
                <button 
                    className={`${styles.filterTab} ${activeFilter === 'clean_days' ? styles.active : ''}`}
                    onClick={() => setActiveFilter('clean_days')}
                >
                    ✨ Clean Days
                </button>
                <button 
                    className={`${styles.filterTab} ${activeFilter === 'social' ? styles.active : ''}`}
                    onClick={() => setActiveFilter('social')}
                >
                    👥 Social
                </button>
            </div>

            {/* Badges Grid */}
            <div className={styles.badgesGrid}>
                {filteredBadges.map((badge) => (
                    <div 
                        key={badge.code} 
                        className={`${styles.badgeCard} ${badge.earned ? styles.earned : styles.locked}`}
                    >
                        <div className={styles.badgeIcon}>
                            {badge.earned ? badge.icon : '🔒'}
                        </div>
                        <div className={styles.badgeInfo}>
                            <h3 className={styles.badgeName}>{badge.name}</h3>
                            <p className={styles.badgeDescription}>{badge.description}</p>
                            <div className={styles.badgeMeta}>
                                <span className={styles.badgePoints}>+{badge.points} pts</span>
                                {badge.earned && (
                                    <span className={styles.earnedBadge}>✓ Earned</span>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {filteredBadges.length === 0 && (
                <div className={styles.emptyState}>
                    <p>No badges found for this filter</p>
                </div>
            )}

            {/* Recently Earned Section */}
            {badgeData?.earned_badges?.length > 0 && activeFilter === 'all' && (
                <div className={styles.recentSection}>
                    <h2 className={styles.sectionTitle}>🎉 Recently Earned</h2>
                    <div className={styles.recentBadges}>
                        {badgeData.earned_badges.slice(0, 5).map((badge) => (
                            <div key={badge.code} className={styles.recentBadge}>
                                <span className={styles.recentIcon}>{badge.icon}</span>
                                <span className={styles.recentName}>{badge.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Badges;
